const FIRST_TOUCH_STORAGE_KEY = "mlx:first-touch";
const LAST_TOUCH_STORAGE_KEY = "mlx:last-touch";
const PUBLIC_FTAID_COOKIE = "ft_aid_pub";
const ATTRIBUTION_KEYS = [
  "gclid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "wbraid",
  "gbraid",
  "campaign_id",
  "ad_group_id",
  "ad_id",
  "ad_account_id",
];

const COOKIE_TTL_DAYS = 180;

const isBrowser = () => typeof window !== "undefined";

const readCookie = (name: string): string | undefined => {
  if (!isBrowser()) return undefined;
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  if (!match) return undefined;
  const v = match[2];
  return typeof v === "string" ? decodeURIComponent(v) : undefined;
};

const writeCookie = (name: string, value: string, days: number) => {
  if (!isBrowser()) return;
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  // Set Domain for cross-subdomain continuity in prod (e.g., .mlx.systems). Avoid on localhost.
  const host = window.location.hostname;
  const isLocal =
    host === "localhost" || host === "127.0.0.1" || host.endsWith(".local");
  const parts = host.split(".").filter(Boolean);
  const base = parts.length >= 2 ? `.${parts.slice(-2).join(".")}` : host;
  const domainAttr = isLocal ? "" : `; Domain=${base}`;
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; SameSite=Lax; Secure; Expires=${expires.toUTCString()}${domainAttr}`;
};

const readJson = <T>(key: string): T | undefined => {
  if (!isBrowser()) return undefined;
  try {
    if (
      typeof window.localStorage === "undefined" ||
      typeof window.localStorage.getItem !== "function"
    ) {
      return undefined;
    }
    const value = window.localStorage.getItem(key);
    if (!value) return undefined;
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
};

const writeJson = (key: string, value: unknown) => {
  if (!isBrowser()) return;
  try {
    if (
      typeof window.localStorage === "undefined" ||
      typeof window.localStorage.setItem !== "function"
    ) {
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

export interface AttributionContext {
  ftAid: string;
  gclid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  wbraid?: string;
  gbraid?: string;
  campaign_id?: string;
  ad_group_id?: string;
  ad_id?: string;
  ad_account_id?: string;
  landing_page?: string;
  referrer?: string;
  timestamp: number;
}

export interface AttributionSubmission {
  ft_aid: string;
  first_touch?: AttributionContext;
  last_touch?: AttributionContext;
}

const ensureFtAid = (): string => {
  if (!isBrowser()) return "server";
  let ftAid = readCookie(PUBLIC_FTAID_COOKIE);
  if (!ftAid) {
    ftAid = crypto.randomUUID();
    writeCookie(PUBLIC_FTAID_COOKIE, ftAid, COOKIE_TTL_DAYS);
  }
  return ftAid;
};

const buildContext = (
  params: URLSearchParams,
  landingUrl: URL,
): Omit<AttributionContext, "ftAid"> | undefined => {
  const data: Record<string, string> = {};
  let hasAttribution = false;
  for (const key of ATTRIBUTION_KEYS) {
    const value = params.get(key);
    if (value) {
      hasAttribution = true;
      data[key] = value;
    }
  }
  if (!hasAttribution) return undefined;
  data.landing_page = landingUrl.toString();
  if (document.referrer) data.referrer = document.referrer;
  return { ...(data as Record<string, string>), timestamp: Date.now() };
};

export const captureAttributionFromUrl = () => {
  if (!isBrowser()) return;
  const url = new URL(window.location.href);
  const context = buildContext(url.searchParams, url);
  const ftAid = ensureFtAid();

  if (context) {
    const storedFirstTouch = readJson<AttributionContext>(
      FIRST_TOUCH_STORAGE_KEY,
    );
    if (!storedFirstTouch) {
      writeJson(FIRST_TOUCH_STORAGE_KEY, { ftAid, ...context });
    }
    writeJson(LAST_TOUCH_STORAGE_KEY, { ftAid, ...context });
  }

  if (!readJson<AttributionContext>(FIRST_TOUCH_STORAGE_KEY)) {
    writeJson(FIRST_TOUCH_STORAGE_KEY, {
      ftAid,
      landing_page: url.toString(),
      referrer: document.referrer || undefined,
      timestamp: Date.now(),
    });
  }
};

export const getAttributionContext = () => {
  const ftAid = ensureFtAid();
  const last = readJson<AttributionContext>(LAST_TOUCH_STORAGE_KEY);
  const first = readJson<AttributionContext>(FIRST_TOUCH_STORAGE_KEY);
  return { ftAid, firstTouch: first, lastTouch: last };
};

export const getAttributionSubmission = (): AttributionSubmission => {
  const attribution = getAttributionContext();
  return {
    ft_aid: attribution.ftAid,
    first_touch: attribution.firstTouch,
    last_touch: attribution.lastTouch,
  };
};

export const recordEventTouch = (context: Partial<AttributionContext>) => {
  if (!isBrowser()) return;
  const ftAid = ensureFtAid();
  const entry = {
    ftAid,
    ...context,
    timestamp: Date.now(),
  } as AttributionContext;
  writeJson(LAST_TOUCH_STORAGE_KEY, entry);
};
